import type { RefObject } from 'react'

import { useEffect, useLayoutEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

/**
 * Layout effect on the client, plain effect on the server. Running the setup
 * before paint is what stops revealed elements flashing at full opacity for a
 * frame before GSAP hides them.
 */
export const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

let registered = false

export const registerGsap = (): void => {
    if (registered || typeof window === 'undefined') return

    gsap.registerPlugin(ScrollTrigger, SplitText)
    registered = true
}

export const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined') return false

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export const EASE = 'expo.out'
export const EASE_SOFT = 'power3.out'

/**
 * The original site's appear animations all run on cubic-bezier(.215,.61,.355,1),
 * which is the bezier form of an ease-out cubic — GSAP's power3.out.
 */
export const EASE_ENTER = 'power3.out'

const num = (el: Element, attr: string, fallback: number): number => {
    const raw = el.getAttribute(attr)
    if (raw === null || raw.trim() === '') return fallback

    const parsed = Number.parseFloat(raw)

    return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * One hook drives every scroll reveal on the site. Mark up the JSX with data
 * attributes and this wires the tween, so timing and easing stay identical
 * across every page.
 *
 *   data-anim="rise" | "fade" | "mask" | "lines" | "words" | "chars" | "scale"
 *   data-anim-delay="0.15"        seconds
 *   data-anim-duration="1.2"      seconds
 *   data-anim-from="80"           distance in px for rise / lines
 *   data-anim-stagger="0.06"      stagger between children of a group
 *   data-anim-group                direct children animate as a stagger
 *   data-anim-start="top 85%"     ScrollTrigger start
 *   data-parallax="0.12"          fraction of viewport height travelled
 *   data-marquee="40"             seconds per loop, negative reverses
 */
export const useSiteAnimations = (scope: RefObject<HTMLElement | null>, deps: unknown[] = []): void => {
    useIsoLayoutEffect(() => {
        const root = scope.current
        if (!root) return

        registerGsap()

        if (prefersReducedMotion()) {
            root.querySelectorAll<HTMLElement>('[data-anim]').forEach((el) => {
                el.style.opacity = '1'
            })

            return
        }

        const ctx = gsap.context(() => {
            /* -------------------------------------------------- split text */

            root.querySelectorAll<HTMLElement>('[data-anim="lines"], [data-anim="words"], [data-anim="chars"]').forEach((el) => {
                const mode = el.getAttribute('data-anim')
                const type = mode === 'lines' ? 'lines' : mode === 'words' ? 'lines,words' : 'lines,words,chars'

                gsap.set(el, { opacity: 1 })

                // eslint-disable-next-line no-new
                new SplitText(el, {
                    type,
                    mask: 'lines',
                    autoSplit: true,
                    onSplit: (self) => {
                        const targets = mode === 'lines' ? self.lines : mode === 'words' ? self.words : self.chars

                        return gsap.from(targets, {
                            yPercent: 118,
                            duration: num(el, 'data-anim-duration', 1.15),
                            delay: num(el, 'data-anim-delay', 0),
                            ease: EASE,
                            stagger: num(el, 'data-anim-stagger', mode === 'chars' ? 0.02 : 0.08),
                            scrollTrigger: {
                                trigger: el,
                                start: el.getAttribute('data-anim-start') ?? 'top 88%',
                                once: true
                            }
                        })
                    }
                })
            })

            /* --------------------------------------------- simple reveals */

            root.querySelectorAll<HTMLElement>('[data-anim="rise"], [data-anim="fade"], [data-anim="scale"]').forEach((el) => {
                const mode = el.getAttribute('data-anim')
                const distance = num(el, 'data-anim-from', 34)

                const from: gsap.TweenVars = { opacity: 0 }

                if (mode === 'rise') from.y = distance
                if (mode === 'scale') from.scale = 1.06

                gsap.fromTo(el, from, {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: num(el, 'data-anim-duration', 1.1),
                    delay: num(el, 'data-anim-delay', 0),
                    ease: EASE,
                    scrollTrigger: {
                        trigger: el,
                        start: el.getAttribute('data-anim-start') ?? 'top 90%',
                        once: true
                    }
                })
            })

            /* ------------------------------------------------ clip reveals */

            root.querySelectorAll<HTMLElement>('[data-anim="mask"]').forEach((el) => {
                gsap.fromTo(
                    el,
                    { clipPath: 'inset(0% 0% 100% 0%)' },
                    {
                        clipPath: 'inset(0% 0% 0% 0%)',
                        duration: num(el, 'data-anim-duration', 1.3),
                        delay: num(el, 'data-anim-delay', 0),
                        ease: EASE,
                        scrollTrigger: {
                            trigger: el,
                            start: el.getAttribute('data-anim-start') ?? 'top 92%',
                            once: true
                        }
                    }
                )

                const inner = el.querySelector<HTMLElement>('[data-anim-inner]')

                if (inner) {
                    gsap.fromTo(
                        inner,
                        { scale: 1.24 },
                        {
                            scale: 1,
                            duration: num(el, 'data-anim-duration', 1.5),
                            delay: num(el, 'data-anim-delay', 0),
                            ease: EASE,
                            scrollTrigger: {
                                trigger: el,
                                start: el.getAttribute('data-anim-start') ?? 'top 92%',
                                once: true
                            }
                        }
                    )
                }
            })

            /* ------------------------------------------------- stagger sets */

            root.querySelectorAll<HTMLElement>('[data-anim-group]').forEach((group) => {
                const items = group.querySelectorAll<HTMLElement>(':scope > *')
                if (items.length === 0) return

                gsap.fromTo(
                    items,
                    { opacity: 0, y: num(group, 'data-anim-from', 42) },
                    {
                        opacity: 1,
                        y: 0,
                        duration: num(group, 'data-anim-duration', 1.05),
                        delay: num(group, 'data-anim-delay', 0),
                        ease: EASE,
                        stagger: num(group, 'data-anim-stagger', 0.09),
                        scrollTrigger: {
                            trigger: group,
                            start: group.getAttribute('data-anim-start') ?? 'top 88%',
                            once: true
                        }
                    }
                )
            })

            /* ---------------------------------------------------- parallax */

            root.querySelectorAll<HTMLElement>('[data-parallax]').forEach((el) => {
                const amount = num(el, 'data-parallax', 0.12)

                gsap.fromTo(
                    el,
                    { yPercent: -amount * 50 },
                    {
                        yPercent: amount * 50,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: el.parentElement ?? el,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                )
            })

            /* ------------------------------------------- scroll-scrubbed -- */

            /**
             * Continuous scroll-linked movement. Unlike the reveal tweens above
             * these are tied to the scrollbar, so scrolling back up plays them
             * in reverse. Offsets are viewport-relative and re-measured on
             * resize, and a small scrub value smooths the interpolation.
             *
             *   data-scrub="enter"          offset -> rest as it scrolls in
             *   data-scrub="exit"           rest -> offset as its scope leaves
             *   data-scrub-x="-55"          horizontal offset, in vw
             *   data-scrub-y="18"           vertical offset, in vh
             *   data-scrub-scale="1.8"
             *   data-scrub-rotate="-4"
             *   data-scrub-smooth="0.6"     seconds of catch-up
             *   data-scrub-start / -end     ScrollTrigger positions
             *   data-scrub-scope            on an ancestor, to share a trigger
             */
            root.querySelectorAll<HTMLElement>('[data-scrub]').forEach((el) => {
                const exiting = el.getAttribute('data-scrub') === 'exit'

                const trigger =
                    el.getAttribute('data-scrub-trigger') === 'section'
                        ? (el.closest('section') ?? el)
                        : (el.closest('[data-scrub-scope]') ?? el.closest('section') ?? el)

                const xVw = num(el, 'data-scrub-x', 0)
                const yVh = num(el, 'data-scrub-y', 0)

                /**
                 * `data-scrub-cover` replaces a fixed scale with the factor that
                 * makes the element fill the viewport, measured from its own
                 * untransformed size so it stays right at any window size. Its
                 * value is an optional multiplier for elements that sit away
                 * from the viewport centre and so need to overshoot.
                 */
                const covers = el.hasAttribute('data-scrub-cover')
                const overshoot = num(el, 'data-scrub-cover', 1)

                const coverScale = () => {
                    const w = el.offsetWidth || 1
                    const h = el.offsetHeight || 1

                    return Math.max(window.innerWidth / w, window.innerHeight / h) * overshoot
                }

                const offset: gsap.TweenVars = {
                    x: () => (window.innerWidth * xVw) / 100,
                    y: () => (window.innerHeight * yVh) / 100,
                    scale: covers ? coverScale : num(el, 'data-scrub-scale', 1),
                    rotate: num(el, 'data-scrub-rotate', 0),
                    ease: 'none'
                }

                const rest: gsap.TweenVars = { x: 0, y: 0, scale: 1, rotate: 0, ease: 'none' }

                // Only touched when asked for, so every other scrub leaves
                // opacity alone and cannot fight an entrance tween.
                if (el.hasAttribute('data-scrub-opacity')) {
                    offset.opacity = num(el, 'data-scrub-opacity', 0)
                    rest.opacity = 1
                }

                const scrollTrigger: ScrollTrigger.Vars = {
                    trigger,
                    start: el.getAttribute('data-scrub-start') ?? (exiting ? 'top top' : 'top 88%'),
                    // '+=100%' is the trigger's own height, which stays correct
                    // even when the scope is a sticky section.
                    end: el.getAttribute('data-scrub-end') ?? (exiting ? '+=100%' : 'center 55%'),
                    scrub: num(el, 'data-scrub-smooth', 0.6),
                    invalidateOnRefresh: true
                }

                if (exiting) {
                    gsap.fromTo(el, rest, { ...offset, scrollTrigger })

                    return
                }

                gsap.fromTo(el, offset, { ...rest, scrollTrigger })
            })

            /* ----------------------------------------------------- marquee */

            root.querySelectorAll<HTMLElement>('[data-marquee]').forEach((track) => {
                const seconds = num(track, 'data-marquee', 38)
                const half = track.scrollWidth / 2

                if (half <= 0) return

                gsap.set(track, { x: seconds < 0 ? -half : 0 })

                gsap.to(track, {
                    x: seconds < 0 ? 0 : -half,
                    duration: Math.abs(seconds),
                    ease: 'none',
                    repeat: -1
                })
            })

            ScrollTrigger.refresh()
        }, root)

        return () => ctx.revert()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)
}

/**
 * Entrance choreography for above-the-fold content. Runs once on mount rather
 * than on scroll, so the first screen animates the moment the page loads.
 *
 *   data-enter="1.5"                  start time on the shared timeline
 *   data-enter-mode="rise|fade|mask|chars"
 *   data-enter-y="-200"               travel in px, negative comes from above
 *   data-enter-scale="1.3"            starting scale
 *   data-enter-skew="-10"             starting skewX in degrees
 *   data-enter-opacity="keep"         move without fading
 *   data-enter-duration="1"
 *   data-enter-stagger="0.012"        per character, for mode="chars"
 */
export const useEntrance = (scope: RefObject<HTMLElement | null>): void => {
    useIsoLayoutEffect(() => {
        const root = scope.current
        if (!root) return

        registerGsap()

        const items = root.querySelectorAll<HTMLElement>('[data-enter]')
        if (items.length === 0) return

        if (prefersReducedMotion()) {
            items.forEach((el) => {
                el.style.opacity = '1'
            })

            return
        }

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ defaults: { ease: EASE_ENTER } })

            items.forEach((el) => {
                const at = num(el, 'data-enter', 0)
                const mode = el.getAttribute('data-enter-mode') ?? 'rise'
                const duration = num(el, 'data-enter-duration', 1)

                if (mode === 'mask') {
                    tl.fromTo(
                        el,
                        { clipPath: 'inset(0% 0% 100% 0%)' },
                        { clipPath: 'inset(0% 0% 0% 0%)', duration: duration * 1.5 },
                        at
                    )

                    return
                }

                /* Per-character reveal: fade up out of a blur, one letter at a
                   time, with words kept unbreakable so lines break normally. */
                if (mode === 'chars') {
                    gsap.set(el, { opacity: 1 })

                    // eslint-disable-next-line no-new
                    new SplitText(el, {
                        type: 'words,chars',
                        wordsClass: 'enter-word',
                        autoSplit: true,
                        onSplit: (self) =>
                            gsap.fromTo(
                                self.chars,
                                { opacity: 0, filter: 'blur(10px)', y: 24 },
                                {
                                    opacity: 1,
                                    filter: 'blur(0px)',
                                    y: 0,
                                    duration,
                                    delay: at,
                                    ease: EASE_ENTER,
                                    stagger: num(el, 'data-enter-stagger', 0.012)
                                }
                            )
                    })

                    return
                }

                const from: gsap.TweenVars = {}

                if (el.getAttribute('data-enter-opacity') !== 'keep') from.opacity = 0
                if (mode !== 'fade') from.y = num(el, 'data-enter-y', num(el, 'data-enter-from', 40))

                const scale = num(el, 'data-enter-scale', 1)
                const skew = num(el, 'data-enter-skew', 0)

                if (scale !== 1) from.scale = scale
                if (skew !== 0) from.skewX = skew

                tl.fromTo(el, from, { opacity: 1, y: 0, scale: 1, skewX: 0, duration }, at)
            })
        }, root)

        return () => ctx.revert()
    }, [scope])
}

/** Infinite horizontal drag/scroll rail used by the solution and journal grids. */
export const useDragRail = (ref: RefObject<HTMLElement | null>): void => {
    useEffect(() => {
        const el = ref.current
        if (!el) return

        let down = false
        let dragging = false
        let startX = 0
        let startScroll = 0

        const onDown = (e: PointerEvent) => {
            if (e.pointerType !== 'mouse') return

            down = true
            dragging = false
            startX = e.clientX
            startScroll = el.scrollLeft
        }

        const onMove = (e: PointerEvent) => {
            if (!down) return

            const delta = e.clientX - startX

            // Only take over once the pointer has genuinely moved, so a plain
            // click on a card inside the rail still reaches the link.
            if (!dragging) {
                if (Math.abs(delta) < 5) return

                dragging = true
                el.setPointerCapture(e.pointerId)
                el.style.cursor = 'grabbing'
            }

            el.scrollLeft = startScroll - delta
        }

        const onUp = (e: PointerEvent) => {
            down = false
            dragging = false

            if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
            el.style.cursor = ''
        }

        el.addEventListener('pointerdown', onDown)
        el.addEventListener('pointermove', onMove)
        el.addEventListener('pointerup', onUp)
        el.addEventListener('pointercancel', onUp)

        return () => {
            el.removeEventListener('pointerdown', onDown)
            el.removeEventListener('pointermove', onMove)
            el.removeEventListener('pointerup', onUp)
            el.removeEventListener('pointercancel', onUp)
        }
    }, [ref])
}

/** Shared ref helper so pages can scope their animations without boilerplate. */
export const useAnimatedPage = () => {
    const ref = useRef<HTMLDivElement>(null)

    useEntrance(ref)
    useSiteAnimations(ref)

    return ref
}
